import React, { useEffect, useState } from 'react';
import Card from './Card';
import ContactMap from './ContactMap';
import {
  getPublicContactSettings,
  FALLBACK_CONTACT_SETTINGS,
  type ContactSettings,
} from '../features/contactSettings';

interface ContactSectionProps {
  /** When true, only show contact info (no banking or special programs). Used on Contact page. */
  compact?: boolean;
}

const ContactSection: React.FC<ContactSectionProps> = ({ compact = false }) => {
  const [settings, setSettings] = useState<ContactSettings>(FALLBACK_CONTACT_SETTINGS);
  const mapsQuery = encodeURIComponent(settings.address);

  useEffect(() => {
    getPublicContactSettings()
      .then(setSettings)
      .catch((err) => {
        console.error(err);
        setSettings(FALLBACK_CONTACT_SETTINGS);
      });
  }, []);

  return (
    <div className="container my-4 my-md-5 px-3 px-md-0">
      <section id="contact" className="mb-5">
        <h3 className="text-center mb-4 section-subtitle">Get in Touch</h3>
        <div className="row g-4 justify-content-center align-items-stretch">
          <div className={compact ? 'col-12 col-lg-6 order-1 order-lg-1' : 'col-12 col-lg-6'}>
            <Card title="Contact Information" icon="bi bi-geo-alt-fill" className={compact ? 'contact-card-centered h-100' : ''}>
              <div className={compact ? 'text-center' : ''}>
                <div className="mb-3">
                  <h6 className="text-primary">Address</h6>
                  <p className="mb-0">
                    {settings.address}
                    {compact && (
                      <>
                        {' '}
                        <a
                          href={`https://maps.google.com/?q=${mapsQuery}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                          title="View on Google Maps"
                        >
                          <i className="bi bi-geo-alt-fill ms-1"></i> View on Map
                        </a>
                      </>
                    )}
                    {!compact && (
                      <a
                        href={`https://maps.google.com/?q=${mapsQuery}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-link p-0 ms-2 text-primary"
                        title="View on Google Maps"
                      >
                        <i className="bi bi-geo-alt-fill fs-5"></i>
                      </a>
                    )}
                  </p>
                </div>
                <div className="mb-3">
                  <h6 className="text-primary">Email</h6>
                  <p className="mb-0">
                    <a href={`mailto:${settings.emailAddress}`}>{settings.emailAddress}</a>
                  </p>
                </div>
                <div>
                  <h6 className="text-primary">Phone</h6>
                  <p className="mb-1">{settings.contactNumber}</p>
                  <p className="mb-1">{settings.whatsappNumber} (WhatsApp)</p>
                  {settings.chairmanPhone && (
                    <p className="mb-1">{settings.chairmanPhone} - Chairman</p>
                  )}
                  {settings.treasurerPhone && (
                    <p className="mb-1">{settings.treasurerPhone} - Treasurer</p>
                  )}
                  {settings.secretaryPhone && (
                    <p className="mb-0">{settings.secretaryPhone} - Secretary</p>
                  )}
                </div>
              </div>
            </Card>
          </div>
          {compact && (
            <div className="col-12 col-lg-6 order-2 order-lg-2">
              <div className="contact-map-wrap contact-map-wrap--side h-100">
                {process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ? (
                  <ContactMap className="contact-map-iframe" />
                ) : (
                  <iframe
                    title="BMCS location map"
                    src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="contact-map-iframe"
                  />
                )}
              </div>
            </div>
          )}
          {!compact && (
            <div className="col-12 col-lg-6">
              <Card title="Banking Details" icon="bi bi-bank">
                <div className="mb-2">
                  <strong>Account Name:</strong><br />
                  <span className="text-muted">{settings.accountName}</span>
                </div>
                <div className="mb-2">
                  <strong>Account Number:</strong><br />
                  <span className="text-muted">{settings.accountNumber}</span>
                </div>
                <div className="mb-2">
                  <strong>Bank:</strong><br />
                  <span className="text-muted">{settings.bankName}</span>
                </div>
                <div className="mb-2">
                  <strong>Branch:</strong><br />
                  <span className="text-muted">
                    {settings.branchName} ({settings.branchCode})
                  </span>
                </div>
                {settings.reference && (
                  <div className="mb-2">
                    <strong>Reference:</strong><br />
                    <span className="text-muted">{settings.reference}</span>
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactSection;
